<template lang="pug">
  management-page(:title="$t(`${I18N_PREFIX}.title`)"
                  :creatable="false"
                  :searchable="false"
                  :showHeader="false")
    template(v-slot:body)
      .documents-section
        .documents-section__toolbar
          span.documents-section__title {{ $t(`${I18N_PREFIX}.documents_title`) }}
          aiq-tooltip(:content="$t(`${I18N_PREFIX}.tooltips.documents`)" placement="top")
            i.iq-ico-explanation.help-icon
          .flex-spacer
          aiq-tooltip(:content="$t(`${I18N_PREFIX}.tooltips.download`)" placement="top")
            aiq-button.export(@click="exportDocuments" :disabled="isEmpty(documents)")
              i.iq-ico-export
        aiq-table.scrollable.clickable(:data="documents"
                                       v-loading="loading"
                                       :empty-text="$t(`${I18N_PREFIX}.empty.documents`)"
                                       :default-sort="DEFAULT_SORT"
                                       :row-class-name="rowClassName"
                                       @row-click="selectDocument")
          aiq-table-column(prop="source_reference"
                           :label="$t(`${I18N_PREFIX}.columns.source_reference`)"
                           min-width="320"
                           sortable
                           label-class-name="is-bordered")
            template(v-slot:default="{ row }")
              a.source-link(v-if="isUrl(row.source_reference)"
                            :href="row.source_reference"
                            target="_blank"
                            rel="noopener noreferrer"
                            @click.stop) {{ row.source_reference }}
              span(v-else) {{ row.source_reference }}
          aiq-table-column(prop="query_count"
                           :label="$t(`${I18N_PREFIX}.columns.count`)"
                           width="110"
                           sortable
                           align="center"
                           label-class-name="is-bordered")
          aiq-table-column(prop="last_seen_at"
                           :label="$t(`${I18N_PREFIX}.columns.last_used`)"
                           width="200"
                           sortable
                           label-class-name="is-bordered")
            template(v-slot:default="{ row }")
              span {{ formatDate(row.last_seen_at) }}
      .documents-section.document-detail(v-if="selectedDocument" ref="detailSection")
        .documents-section__toolbar
          span.documents-section__title {{ $t(`${I18N_PREFIX}.detail_title`, { source: selectedDocument.source_reference, count: selectedDocument.query_count, interpolation: { escapeValue: false } }) }}
          .flex-spacer
          aiq-tooltip(:content="$t(`${I18N_PREFIX}.tooltips.download`)" placement="top")
            aiq-button.export(@click="exportQueries" :disabled="isEmpty(selectedDocument.queries)")
              i.iq-ico-export
          aiq-tooltip(:content="$t(`${I18N_PREFIX}.tooltips.close`)" placement="top")
            aiq-button.close-btn(text @click="clearSelection")
              fa-icon(:icon="['fas', 'xmark']" size="lg")
        aiq-table.scrollable(:data="selectedDocument.queries")
          aiq-table-column(prop="query_text"
                           :label="$t(`${I18N_PREFIX}.columns.question`)"
                           min-width="320"
                           label-class-name="is-bordered")
            template(v-slot:default="{ row }")
              span(v-if="row.query_text") {{ row.query_text }}
              span.query-placeholder(v-else) {{ $t(`${I18N_PREFIX}.query_placeholder`, { id: row.query_id }) }}
          aiq-table-column(v-if="showFeedback"
                           prop="liked"
                           :label="$t(`${I18N_PREFIX}.columns.feedback`)"
                           width="110"
                           align="center"
                           label-class-name="is-bordered")
            template(v-slot:default="{ row }")
              aiq-tooltip(v-if="row.liked === true"
                          :content="$t(`${I18N_PREFIX}.feedback.liked`)"
                          placement="top")
                fa-icon.feedback-icon.liked(:icon="['fas', 'thumbs-up']")
              aiq-tooltip(v-else-if="row.liked === false"
                          :content="$t(`${I18N_PREFIX}.feedback.disliked`)"
                          placement="top")
                fa-icon.feedback-icon.disliked(:icon="['fas', 'thumbs-down']")
              span.feedback-empty(v-else) —
          aiq-table-column(v-if="showFeedback"
                           prop="asked_by"
                           :label="$t(`${I18N_PREFIX}.columns.asked_by`)"
                           width="180"
                           sortable
                           label-class-name="is-bordered")
            template(v-slot:default="{ row }")
              span(:class="{ 'feedback-empty': !row.asked_by }") {{ askedByDisplay(row) }}
          aiq-table-column(v-if="showFeedback"
                           prop="source_type"
                           :label="$t(`${I18N_PREFIX}.columns.interface`)"
                           width="140"
                           sortable
                           label-class-name="is-bordered")
            template(v-slot:default="{ row }")
              span(v-if="row.source_type") {{ interfaceLabel(row.source_type) }}
              span.feedback-empty(v-else) —
          aiq-table-column(prop="sent_at"
                           :label="$t(`${I18N_PREFIX}.columns.asked_at`)"
                           width="200"
                           sortable
                           label-class-name="is-bordered")
            template(v-slot:default="{ row }")
              span {{ formatDate(row.sent_at) }}
</template>

<script>
import ManagementPage from '@/components/ManagementPage/ManagementPage.vue';
import { find, get, isEmpty, isString, snakeCase } from 'lodash';
import {
  downloadAsFile,
  useTimeZone,
  getMetricsTimeZoneAbbr,
  formatDateWithTimezone,
} from '@/libs';
import { dstDateRangeUtc } from '../../configs/dateRange';
import dayjs from '@/localPlugins/dayjs';

const I18N_PREFIX = 'metrics_tab.smart_assist_tab.documents_tab';
const DEFAULT_SORT = { prop: 'query_count', order: 'descending' };
const DATE_FORMAT = 'MMM DD, YYYY hh:mm A';

export default {
  pageResource: '/metrics',
  components: {
    ManagementPage,
  },
  props: {
    dateRange: { type: Array, required: true },
    filters: { type: Array, default: () => [] },
    metric: { type: String, default: 'smart_assist_documents' },
    showFeedback: { type: Boolean, default: true },
  },
  data() {
    return {
      I18N_PREFIX,
      DEFAULT_SORT,
      documents: [],
      loading: false,
      selectedSourceReference: null,
      timezone: '',
      tzAbbr: '',
    };
  },
  async mounted() {
    this.timezone = await useTimeZone(
      Intl.DateTimeFormat().resolvedOptions().timeZone,
      this.$store.dispatch,
      this.$store._state.data.configs,
    );
    this.tzAbbr = getMetricsTimeZoneAbbr(this.timezone);
    this.fetchDocuments();
  },
  computed: {
    dateRangeUtc() {
      return dstDateRangeUtc(this.dateRange);
    },
    selectedDocument() {
      if (this.selectedSourceReference === null) return null;
      return find(this.documents, { source_reference: this.selectedSourceReference }) || null;
    },
  },
  watch: {
    dateRangeUtc() {
      this.fetchDocuments();
    },
    filters() {
      this.fetchDocuments();
    },
  },
  methods: {
    isEmpty,
    isUrl(value) {
      return isString(value) && /^https?:\/\//i.test(value);
    },
    formatDate(d) {
      if (!d) return '';
      return `${formatDateWithTimezone(d, this.timezone, DATE_FORMAT)} ${this.tzAbbr}`;
    },
    feedbackLabel(liked) {
      if (liked === true) return this.$t(`${I18N_PREFIX}.feedback.liked`);
      if (liked === false) return this.$t(`${I18N_PREFIX}.feedback.disliked`);
      return '';
    },
    interfaceLabel(sourceType) {
      if (!sourceType) return '';
      return this.$t(`metrics_tab.smart_assist_tab.source_type_filter.options.${sourceType}`, sourceType);
    },
    askedByDisplay(row) {
      return row.asked_by || '—';
    },
    queryDisplay(row) {
      return row.query_text
        || this.$t(`${I18N_PREFIX}.query_placeholder`, { id: row.query_id });
    },
    selectDocument(row) {
      if (this.selectedSourceReference === row.source_reference) {
        this.clearSelection();
        return;
      }
      this.selectedSourceReference = row.source_reference;
      this.scrollToDetail();
    },
    scrollToDetail() {
      this.$nextTick(() => {
        requestAnimationFrame(() => {
          this.$refs.detailSection?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        });
      });
    },
    clearSelection() {
      this.selectedSourceReference = null;
    },
    rowClassName({ row }) {
      return this.selectedSourceReference === row.source_reference ? 'is-selected' : '';
    },
    returnReportFileName(chartName = 'data') {
      const [startTimestamp, endTimestamp] = this.dateRange;
      const startDate = dayjs.isDayjs(startTimestamp)
        ? startTimestamp.format('YYYY-MM-DD')
        : dayjs(startTimestamp).format('YYYY-MM-DD');
      const endDate = dayjs.isDayjs(endTimestamp)
        ? endTimestamp.format('YYYY-MM-DD')
        : dayjs(endTimestamp).format('YYYY-MM-DD');
      return `${chartName.replaceAll(' ', '_')}_${startDate}_${endDate}.csv`;
    },
    exportDocuments() {
      const rows = this.documents.map(d => ({
        Resource: d.source_reference,
        Questions: d.query_count,
        'Last used': this.formatDate(d.last_seen_at),
      }));
      downloadAsFile({
        filename: this.returnReportFileName(this.metric),
        data: rows,
        dataType: 'table',
      });
    },
    exportQueries() {
      const doc = this.selectedDocument;
      if (!doc) return;
      const rows = get(doc, 'queries', []).map(q => ({
        Question: this.queryDisplay(q),
        ...(this.showFeedback ? {
          Feedback: this.feedbackLabel(q.liked),
          'Asked by': q.asked_by || '',
          Interface: this.interfaceLabel(q.source_type),
        } : {}),
        'Asked on': this.formatDate(q.sent_at),
      }));
      downloadAsFile({
        filename: this.returnReportFileName(`${this.metric}_${snakeCase(doc.source_reference)}`),
        data: rows,
        dataType: 'table',
      });
    },
    async fetchDocuments() {
      const [period_start, period_end] = this.dateRangeUtc;
      if (!(period_start && period_end)) {
        return;
      }
      this.loading = true;
      this.clearSelection();
      try {
        const data = await this.$store.dispatch('metrics/getMetric', [
          this.metric,
          { period_start, period_end, bucket: 'day', filters: this.filters },
        ]);
        const dataset = get(data, 'dataset', []);
        this.documents = get(find(dataset, { metric: this.metric }), 'data_points', []);
      } catch (err) {
        this.$aiq.notify.error(this.$t(`${I18N_PREFIX}.errors.load_failed`));
        this.documents = [];
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import "../../../../styles/aiq-variables.scss";

.documents-section {
  margin-bottom: 16px;
}

.documents-section__toolbar {
  display: flex;
  align-items: center;
  margin-bottom: 8px;
}

.documents-section__title {
  font-size: 14px;
  font-weight: 600;
  color: $aiq-text-active;
}

.help-icon {
  margin-left: 6px;
  vertical-align: middle;
}

.flex-spacer {
  flex: 1;
}

.source-link {
  color: $aiq-bgc-button-primary;
  text-decoration: none;
  word-break: break-all;

  &:hover {
    text-decoration: underline;
  }
}

.query-placeholder {
  color: $aiq-color-text-muted;
  font-style: italic;
}

:deep(.el-button.export) {
  height: 30px;
  width: 40px;
  padding: 0;
  margin-left: 8px;
}

:deep(.el-button.close-btn) {
  height: 30px;
  width: 30px;
  padding: 0;
  margin-left: 4px;
}

.document-detail {
  margin-top: 16px;
}

.feedback-icon {
  font-size: 14px;

  &.liked {
    color: $aiq-color-success;
  }

  &.disliked {
    color: $aiq-color-error;
  }
}

.feedback-empty {
  color: $aiq-color-text-muted;
}
</style>

<style lang="scss" scoped>
@import "../../../../styles/aiq-mixins.scss";
@import "../../../../styles/aiq-variables.scss";

.documents-section {
  .scrollable :deep(.el-table__body-wrapper) {
    @include scrollable(calc(100vh - 280px));
  }

  &.document-detail .scrollable :deep(.el-table__body-wrapper) {
    @include scrollable(420px);
  }
}

.clickable {
  :deep(.el-table__row) {
    cursor: pointer;
  }
  :deep(.el-table__row.is-selected td) {
    background-color: $aiq-bgc-sidebar-item-hover !important;
  }
}
</style>
